import { SectionHeader } from '../components/shared/SectionHeader';
import { ModuleCard } from '../components/shared/ModuleCard';
import { CTAStrip } from '../components/shared/CTAStrip';
import {
  FileText,
  ClipboardCheck,
  Globe,
  Upload,
  Layout,
  Bell,
  CreditCard,
  BarChart3
} from 'lucide-react';

const modules = [
  {
    icon: FileText,
    title: "Phyto Certificate Applications",
    description: "Complete application management from submission to approval. Exporters can submit applications with required documents, while the system validates against export requirements automatically.",
    users: "Exporters, Administrators",
    features: [
      "Guided application forms with validation",
      "Document upload and checklist management",
      "Status tracking and history",
      "Automatic requirement verification"
    ],
    inputs: "Application data, supporting documents",
    outputs: "Validated application records, status updates",
    variant: "large"
  },
  {
    icon: ClipboardCheck,
    title: "Inspector Dashboard & Allocation",
    description: "Queue-based inspection management with flexible assignment options. Inspectors see their assigned work, record findings, and submit assessments through a streamlined interface.",
    users: "Inspectors, Supervisors",
    features: [
      "Queue by office location",
      "Individual or pooled assignment",
      "Findings and notes capture",
      "Evidence attachment support"
    ],
    inputs: "Approved applications, inspector availability",
    outputs: "Inspection reports, recommendations",
    variant: "large"
  },
  {
    icon: Globe,
    title: "Export Requirements (Rules Engine)",
    description: "Configurable rules for commodity and country-specific export requirements. Ensures every application is validated against the correct criteria for its destination.",
    users: "Administrators, Regulators",
    features: [
      "Commodity-based rules configuration",
      "Country-specific requirements",
      "Automatic validation triggers",
      "Version control for rule changes"
    ],
    inputs: "Commodity data, destination countries, regulatory requirements",
    outputs: "Validation results, requirement checklists",
    variant: "default"
  },
  {
    icon: Upload,
    title: "Document Library & Evidence",
    description: "Centralized storage for all supporting documents and inspection evidence. Supports multiple file types with secure access controls and organized retrieval.",
    users: "All users",
    features: [
      "PDF, JPG, PNG, Excel support",
      "Secure upload and storage",
      "Preview functionality",
      "Linked to applications and inspections"
    ],
    inputs: "Documents, photos, spreadsheets",
    outputs: "Organized document records, access logs",
    variant: "default"
  },
  {
    icon: Layout,
    title: "Template Builder",
    description: "Official certificate templates with fixed layouts that maintain consistent formatting. Supports placeholders, annexures for overflow content, and security features.",
    users: "Administrators",
    features: [
      "Fixed-layout certificate templates",
      "Placeholder field mapping",
      "Annexure support for overflow",
      "Watermark and security text"
    ],
    inputs: "Template designs, placeholder definitions",
    outputs: "Official certificate PDFs",
    variant: "wide"
  },
  {
    icon: Bell,
    title: "Notifications & Messaging",
    description: "Event-driven notification system keeping all stakeholders informed. Email alerts and in-app notifications triggered by workflow events.",
    users: "All users",
    features: [
      "Email notifications",
      "In-app alerts",
      "Event-driven triggers",
      "Configurable notification rules"
    ],
    inputs: "Workflow events, user preferences",
    outputs: "Email alerts, notification logs",
    variant: "default"
  },
  {
    icon: CreditCard,
    title: "Billing & Accounts",
    description: "Service-based billing with credit management. Track service fees, manage account balances, and set threshold notifications for low credits.",
    users: "Exporters, Finance, Administrators",
    features: [
      "Service-based fee tracking",
      "Credit-only payment model",
      "30-day settlement option",
      "Low balance threshold alerts"
    ],
    inputs: "Service requests, payment records",
    outputs: "Invoices, account statements",
    variant: "default"
  },
  {
    icon: BarChart3,
    title: "Reporting & Analytics",
    description: "Comprehensive reporting for operational visibility and compliance metrics. Dashboards for different user roles with relevant KPIs and export capabilities.",
    users: "Supervisors, Administrators, Regulators",
    features: [
      "Role-based dashboards",
      "Compliance metrics tracking",
      "Export to common formats",
      "Trend analysis and insights"
    ],
    inputs: "System data, date ranges, filters",
    outputs: "Reports, charts, exportable data",
    variant: "default"
  }
];

export default function ModulesPage() {
  return (
    <div data-testid="modules-page">
      {/* Hero */}
      <section className="section-padding bg-slate-50 leaf-pattern">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge-green mb-4 inline-block">Platform Modules</span>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              A complete suite for 
              <span className="text-[#1F7A3A]"> certification management</span>
            </h1>
            <p className="text-lg text-slate-600">
              Each module is designed to handle a specific aspect of the phytosanitary certification 
              workflow. Together, they provide an end-to-end solution from application to issuance.
            </p>
          </div>
        </div>
      </section>

      {/* Modules Grid */}
      <section data-testid="modules-grid" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => (
              <ModuleCard
                key={index}
                icon={module.icon}
                title={module.title}
                description={module.description}
                users={module.users}
                features={module.features}
                inputs={module.inputs}
                outputs={module.outputs}
                variant={module.variant}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Integration Note */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <SectionHeader 
              badge="Integrated Platform"
              title="Modules that work together"
              subtitle="All modules share a common data layer and workflow engine, ensuring seamless information flow from application intake through certificate issuance."
            />
            
            <div className="mt-12 p-8 bg-white rounded-2xl shadow-card">
              <div className="flex flex-wrap justify-center gap-8 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#1F7A3A]" />
                  Unified data model
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#C8A24A]" />
                  Event-driven workflow
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-400" />
                  Role-based access
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#1F7A3A]" />
                  Complete audit trails
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTAStrip 
        title="See all modules in action"
        subtitle="Request a comprehensive demo to explore how each module can streamline your certification workflows."
      />
    </div>
  );
}
